import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import googleIcon from '../assets/google-icon.svg';
import logoImage from '../assets/Frame 1000005895.png';
import axios from 'axios';
import { useAuth } from '../context/AuthContext'; // Import the auth context
import { signUpWithGoogle, signInWithGoogle } from '../services/api';

// Create context to manage login modal visibility globally
export const AuthModalContext = React.createContext();

export const AuthModalProvider = ({ children }) => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  
  const openLoginModal = () => setIsLoginModalOpen(true);
  const closeLoginModal = () => setIsLoginModalOpen(false);
  
  return (
    <AuthModalContext.Provider value={{ isLoginModalOpen, openLoginModal, closeLoginModal }}>
      {children}
      <LoginModal isOpen={isLoginModalOpen} onClose={closeLoginModal} />
    </AuthModalContext.Provider>
  );
};

const LoginModal = ({ isOpen, onClose }) => {
  // State variables
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const navigate = useNavigate();
  
  // Import auth functions from context
  const { createUser, signInWithEmail } = useAuth();
  
  // Function to show success popup
  const showSuccess = (message) => {
    setSuccessMessage(message);
    setShowSuccessPopup(true);
    
    // Automatically hide popup after 3 seconds
    setTimeout(() => {
      setShowSuccessPopup(false);
    }, 3000);
  };

  // Handle Google Authentication
  const handleGoogleSignIn = async () => {
    try {
      //setIsLoading(true);
      setErrorMessage('');
      
      console.log("🔐 Google Sign-In/Sign-Up: Starting authentication");
      
      // We need to load Firebase SDK dynamically
      if (typeof window.firebase === 'undefined') {
        console.log("Loading Firebase SDK...");
        
        // Load Firebase App
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://www.gstatic.com/firebasejs/8.10.0/firebase-app.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
        
        // Load Firebase Auth
        await new Promise((resolve, reject) => {
          const script = document.createElement('script');
          script.src = 'https://www.gstatic.com/firebasejs/8.10.0/firebase-auth.js';
          script.onload = resolve;
          script.onerror = reject;
          document.head.appendChild(script);
        });
        
        console.log("Firebase SDK loaded successfully");
      }
      
      // Initialize Firebase if not already initialized
      if (!window.firebase.apps || !window.firebase.apps.length) {
        const firebaseConfig = {
          apiKey: "AIzaSyB-Ga9bUSax9HjI_DNtXMvjguS6hgK1YAg",
          authDomain: "game-ai-504f3.firebaseapp.com",
          projectId: "game-ai-504f3",
        };
        
        window.firebase.initializeApp(firebaseConfig);
        console.log("Firebase initialized successfully");
      }
      
      // Use Firebase for Google authentication
      const auth = window.firebase.auth();
      const provider = new window.firebase.auth.GoogleAuthProvider();
      
      // Sign in with popup
      console.log("Opening Google sign-in popup...");
      const result = await auth.signInWithPopup(provider);
      
      // Get token
      const token = await result.user.getIdToken();
      console.log("Got ID token from Firebase:", token.substring(0, 10) + "...");

      let response;
      let isSignUp = false;
      
      try {
        // First try to sign in with the existing token
        console.log("Trying sign in first at https://api.lab.battlexo.com/api/v1/auth/google/signin");
        response = await axios.post("https://api.lab.battlexo.com/api/v1/auth/google/signin", {
          token: token
        });
        console.log("✅ Google Sign-in Success:", response.data);
      } catch (signInError) {
        // If sign-in fails with 401, try sign-up instead
        if (signInError.response && signInError.response.status === 401) {
          console.log("Sign-in failed with 401, trying sign-up instead");
          try {
            console.log("Attempting sign up at https://api.lab.battlexo.com/api/v1/auth/google/signup");
            response = await axios.post("https://api.lab.battlexo.com/api/v1/auth/google/signup", {
              token: token
            });
            isSignUp = true;
            console.log("✅ Google Sign-up Success:", response.data);
          } catch (signUpError) {
            console.error("❌ Google Sign-up also failed:", signUpError);
            throw signUpError;
          }
        } else {
          // If it's not a 401 error, rethrow
          console.error("❌ Google Sign-in failed with non-401 error:", signInError);
          throw signInError;
        }
      }
      
      // Save auth data if we have a valid response
      if (response && response.data) {
        if (response.data.token) {
          console.log(`✅ Received authentication token from backend (${isSignUp ? 'sign-up' : 'sign-in'})`, response.data.token.substring(0, 10) + "...");
          localStorage.setItem('authToken', response.data.token);
          
          // Remove any mock flag if it exists
          localStorage.removeItem('mock');
          
          // Create user object from response data
          const userData = {
            id: response.data.user_id || response.data.id || result.user.uid,
            user_id: response.data.user_id || response.data.id || result.user.uid,
            email: response.data.email || result.user.email,
            first_name: response.data.first_name || result.user.displayName?.split(' ')[0] || '',
            last_name: response.data.last_name || result.user.displayName?.split(' ').slice(1).join(' ') || '',
            name: response.data.name || result.user.displayName || '',
            picture: response.data.picture || result.user.photoURL || '',
            loggedIn: true
          };
          
          console.log("Saving user data to localStorage:", userData);
          localStorage.setItem('currentUser', JSON.stringify(userData));
          
          // Show success message
          showSuccess(isSignUp ? "Successfully signed up with Google!" : "Successfully signed in with Google!");
          
          // Delay navigation to allow popup to be seen
          setTimeout(() => {
            onClose();
            navigate('/');
            // Force reload to make sure all components reflect the auth state
            window.location.reload();
          }, 1500);
          
          return true;
        } else if (response.data.success && response.data.user_id) {
          // Handle case where server successfully authenticates but doesn't return a token
          console.log(`⚠️ No token in response but user_id is present. Proceeding with ${isSignUp ? 'sign-up' : 'sign-in'}.`);
          
          // Create a temporary token for the session
          const tempToken = `temp_${Date.now()}_${Math.random().toString(36).substring(2)}`;
          localStorage.setItem('authToken', tempToken);
          
          // Create user object from response data
          const userData = {
            id: response.data.user_id,
            user_id: response.data.user_id,
            email: result.user.email,
            first_name: result.user.displayName?.split(' ')[0] || '',
            last_name: result.user.displayName?.split(' ').slice(1).join(' ') || '',
            name: result.user.displayName || '',
            picture: result.user.photoURL || '',
            loggedIn: true
          };
          
          console.log("Saving user data to localStorage:", userData);
          localStorage.setItem('currentUser', JSON.stringify(userData));
          
          // Show success message
          showSuccess(isSignUp ? "Successfully signed up with Google!" : "Successfully signed in with Google!");
          
          // Delay navigation to allow popup to be seen
          setTimeout(() => {
            onClose();
            navigate('/');
            // Force reload to make sure all components reflect the auth state
            window.location.reload();
          }, 1500);
          
          return true;
        }
      }
      
      // If we get here, we didn't have a valid token in the response
      console.error("No token received from server", response?.data);
      throw new Error("Authentication failed: No token received from server");
    } catch (error) {
      console.error("❌ Google Authentication Failed:", error);
      
      // Show the actual error 
      const errorMessage = error.response?.data?.message || error.message || "Authentication failed";
      console.error("Authentication error details:", errorMessage);
      
      // Clear any existing auth data to prevent using old data
      localStorage.removeItem('authToken');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('mock');
      
      setErrorMessage('Google authentication failed. Please try again.');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form submission using AuthContext functions
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Common validation
    if (!email) {
      setErrorMessage('Please enter your email address');
      return;
    }
    
    if (!password) {
      setErrorMessage('Please enter your password');
      return;
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setErrorMessage('Please enter a valid email address');
      return;
    }
    
    // Additional validation for signup
    if (isSignUp) {
      if (!firstName) {
        setErrorMessage('Please enter your first name');
        return;
      }
      
      if (!lastName) {
        setErrorMessage('Please enter your last name');
        return;
      }
      
      if (!mobileNumber) {
        setErrorMessage('Please enter your mobile number');
        return;
      }
      
      // Simple mobile number validation
      const mobileRegex = /^\d{10}$/;
      if (!mobileRegex.test(mobileNumber)) {
        setErrorMessage('Please enter a valid 10-digit mobile number');
        return;
      }
    }
    
    try {
      setIsLoading(true);
      setErrorMessage('');
      
      if (isSignUp) {
        // Use the createUser function from AuthContext for registration
        const userData = {
          email: email,
          password: password,
          first_name: firstName,
          last_name: lastName,
          mobile_number: mobileNumber
        };
        
        console.log("Registering new user:", userData);
        await createUser(userData);
        
        console.log("Registration successful!");
        showSuccess("Registration successful! Please sign in with your credentials.");
        setIsSignUp(false);
        setPassword(''); // Clear password for security
      } else {
        // Use the signInWithEmail function from AuthContext for login
        console.log("Signing in user:", email);
        await signInWithEmail(email, password);
        
        console.log("Login successful!");
        showSuccess("Successfully signed in!");
        setTimeout(() => {
          onClose();
          navigate('/');
        }, 1500);
      }
    } catch (error) {
      console.error(`❌ ${isSignUp ? 'Registration' : 'Login'} Failed:`, error);
      
      // Special handling for 401 in sign-in mode
      if (!isSignUp && error.response?.status === 401) {
        console.log("User not found. Suggesting sign-up instead...");
        setErrorMessage('Account not found. Please sign up first.');
        setIsSignUp(true);
        setPassword(''); // Clear password for security
        return;
      }
      
      const errorMessage = error.message || `${isSignUp ? 'Registration' : 'Authentication'} failed`;
      console.error("Error details:", errorMessage);
      
      setErrorMessage(`${isSignUp ? 'Sign-up' : 'Sign-in'} failed. ${errorMessage}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  // Toggle between sign-in and sign-up modes
  const toggleAuthMode = () => {
    setIsSignUp(!isSignUp);
    setErrorMessage('');
  };
  
  // Only render the modal when isOpen is true
  if (!isOpen) return null;
  
  return (
    <ModalOverlay 
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>×</CloseButton>
        
        <LogoImage src={logoImage} alt="Logo" />
        
        <TabContainer>
          <Tab active={!isSignUp} onClick={() => setIsSignUp(false)}>Sign In</Tab>
          <Tab active={isSignUp} onClick={() => setIsSignUp(true)}>Sign Up</Tab>
        </TabContainer>
        
        <SignInButton onClick={handleGoogleSignIn} disabled={isLoading}>
          <ButtonIcon src={googleIcon} alt="Google" />
          {isSignUp ? 'Sign up with Google' : 'Sign in with Google'}
        </SignInButton>
        
        <Divider>
          <DividerLine />
          <DividerText>Or</DividerText>
          <DividerLine />
        </Divider>
        
        <EmailForm onSubmit={handleFormSubmit}>
          {isSignUp && (
            <>
              <InputContainer>
                <NameInput
                  type="text"
                  placeholder="First Name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isLoading}
                />
                <NameInput
                  type="text"
                  placeholder="Last Name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isLoading}
                />
              </InputContainer>
              <EmailInput
                type="tel"
                placeholder="Mobile Number"
                value={mobileNumber}
                onChange={(e) => setMobileNumber(e.target.value)}
                disabled={isLoading}
              />
            </>
          )}
          
          <EmailInput
            type="email"
            placeholder="Email Address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
          />
          
          <PasswordInput
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={isLoading}
          />
          
          <EmailButton type="submit" disabled={isLoading}>
            {isLoading ? (isSignUp ? 'Signing up...' : 'Signing in...') : (isSignUp ? 'Sign Up' : 'Sign In')}
          </EmailButton>
        </EmailForm>
        
        {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
        
        <SwitchModeText>
          {isSignUp ? 'Already have an account?' : "Don't have an account?"} 
          <SwitchModeLink onClick={toggleAuthMode}>
            {isSignUp ? 'Sign In' : 'Sign Up'}
          </SwitchModeLink>
        </SwitchModeText>
        
        <TermsText>
          By signing {isSignUp ? 'up' : 'in'}, you agree to our <TermsLink href="/terms" target="_blank">terms & conditions</TermsLink>
        </TermsText>
      </ModalContainer>
      
      {/* Success popup */}
      {showSuccessPopup && (
        <SuccessPopup>
          <SuccessIcon>✓</SuccessIcon>
          <SuccessText>{successMessage}</SuccessText>
        </SuccessPopup>
      )}
    </ModalOverlay>
  );
};

// Styling components for the modal
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  animation: fadeIn 0.3s ease-in-out;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContainer = styled.div`
  background: linear-gradient(to right bottom, #FFF1F9, #E4EAFF);
  border-radius: 16px;
  padding: 30px;
  width: 90%;
  max-width: 450px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
  animation: slideUp 0.3s ease-out;
  position: relative;
  border: 1.5px solid transparent;
  background-image: linear-gradient(to right bottom, #FFF1F9, #E4EAFF), 
                    linear-gradient(to bottom, #0782FE, #E80694);
  background-origin: border-box;
  background-clip: padding-box, border-box;
  
  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 15px;
  right: 15px;
  background: none;
  border: none;
  font-size: 24px;
  cursor: pointer;
  color: #333;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 30px;
  width: 30px;
  border-radius: 50%;
  
  &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
`;

const LogoImage = styled.img`
  height: 40px;
  margin-bottom: 20px;
  object-fit: contain;
`;

const TabContainer = styled.div`
  display: flex;
  width: 100%;
  margin-bottom: 20px;
  border-radius: 8px;
  overflow: hidden;
  background-color: rgba(255, 255, 255, 0.3);
`;

const Tab = styled.button`
  flex: 1;
  padding: 12px;
  text-align: center;
  background-color: ${props => props.active ? 'white' : 'transparent'};
  border: none;
  cursor: pointer;
  font-weight: ${props => props.active ? '600' : '400'};
  color: ${props => props.active ? '#000' : '#555'};
  transition: all 0.3s ease;
  font-family: 'Space Grotesk', sans-serif;
`;

const ButtonIcon = styled.img`
  width: 20px;
  height: 20px;
  margin-right: 10px;
`;

const SignInButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px;
  margin-bottom: 15px;
  background-color: white;
  position: relative;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  font-family: 'Space Grotesk', sans-serif;
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    bottom: 0;
    left: 0;
    right: 0;
    border-radius: 8px;
    padding: 1px;
    background: linear-gradient(to top, #E80694, #0782FE);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
  }
  
  &:hover {
    background-color: #F8F8F8;
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const Divider = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin: 15px 0;
`;

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background-color: #E0E0E0;
`;

const DividerText = styled.span`
  padding: 0 15px;
  color: #888;
  font-size: 14px;
  font-family: 'Space Grotesk', sans-serif;
`;

const EmailForm = styled.form`
  width: 100%;
  margin-bottom: 15px;
  display: flex;
  flex-direction: column;
  gap: 15px;
`;

const InputContainer = styled.div`
  display: flex;
  gap: 15px;
  width: 100%;
`;

const NameInput = styled.input`
  padding: 12px;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  font-size: 16px;
  font-family: 'Space Grotesk', sans-serif;
  width: 100%; /* Make input take available space */
  box-sizing: border-box; /* Include padding and border in element's width calculation */
  
  &:focus {
    outline: none;
    border-color: #5B68DF;
  }
`;
const EmailInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  font-size: 16px;
  font-family: 'Space Grotesk', sans-serif;
  
  &:focus {
    outline: none;
    border-color: #5B68DF;
  }
`;

const PasswordInput = styled.input`
  width: 100%;
  padding: 12px;
  border: 1px solid #E0E0E0;
  border-radius: 8px;
  font-size: 16px;
  font-family: 'Space Grotesk', sans-serif;
  
  &:focus {
    outline: none;
    border-color: #5B68DF;
  }
`;

const EmailButton = styled.button`
  width: 100%;
  padding: 12px;
  background-color: black;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s;
  font-family: 'Space Grotesk', sans-serif;
  
  &:hover {
    background-color: #333;
  }
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #E53935;
  margin-bottom: 15px;
  font-size: 14px;
  text-align: center;
  width: 100%;
  font-family: 'Space Grotesk', sans-serif;
`;

const SwitchModeText = styled.p`
  font-size: 14px;
  color: #555;
  margin: 10px 0;
  font-family: 'Space Grotesk', sans-serif;
`;

const SwitchModeLink = styled.span`
  color: #0782FE;
  margin-left: 5px;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    text-decoration: underline;
  }
`;

const TermsText = styled.p`
  font-size: 14px;
  color: black;
  text-align: center;
  margin-top: 10px;
  font-family: 'Space Grotesk', sans-serif;
`;

const TermsLink = styled.a`
  color: black;
  text-decoration: underline;
  font-weight: 500;
  
  &:hover {
    text-decoration: underline;
  } 
`;

// Success popup styling
const SuccessPopup = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  background: linear-gradient(135deg, #E80694, #0782FE);
  color: black;
  padding: 16px 24px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 10000;
  animation: slideInRight 0.3s ease-out;
  border: 1.5px solid transparent;
  background-image: linear-gradient(to right bottom, #FFF1F9, #E4EAFF), 
                    linear-gradient(to bottom, #0782FE, #E80694);
  background-origin: border-box;
  background-clip: padding-box, border-box;
  
  @keyframes slideInRight {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`;

const SuccessIcon = styled.div`
  background-color: white;
  color: #4CAF50;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 12px;
  font-weight: bold;
`;

const SuccessText = styled.div`
  font-family: 'Space Grotesk', sans-serif;
  font-weight: 500;
  color: black;
`;

export default LoginModal;